import { ContentType, ProductVisibility } from "@coaching-culture/types";
import {
  Box,
  Button,
  Flex,
  Loader,
  Panel,
  SectionHeading,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import CenterColumn from "components/CenterColumn";
import { PageHeader } from "components/PageHeader";
import ShareModal, { ShareConfig } from "components/ShareModal";
import { useProductVisibility } from "hooks/useProductVisibility";
import { sortBy } from "lodash";
import { useUserContent } from "queries/mindset";
import { useOrgUser } from "queries/users";
import React, { useState } from "react";
import { useParams } from "react-router";
import { ContentDetailsTable, LessonDetailsTable } from "./ContentDetailsTable";

type ContentSectionProps = {
  heading: string;
  assignLabel: string;
  onAssign: (() => void) | null;
  children: React.ReactNode;
};

const ContentSection = ({
  heading,
  assignLabel,
  onAssign,
  children,
}: ContentSectionProps) => (
  <Box mb={5}>
    <Flex alignItems={"center"}>
      <SectionHeading>{heading}</SectionHeading>
      {onAssign != null && (
        <Flex mb={3} ml={2} flexDirection={"row-reverse"}>
          <Button onClick={onAssign}>{assignLabel}</Button>
        </Flex>
      )}
    </Flex>
    {children}
  </Box>
);

export const UserProfilePage = () => {
  const [user] = useUser();
  const { id } = useParams<{ id: string }>();
  const userId = id.toString().toLowerCase() === "me" ? user.id : id;
  const [shareConfig, setShareConfig] = useState<ShareConfig | null>(null);
  const { data: userContent, isFetched } = useUserContent(userId);
  const { data: profileUser, isFetched: userFetched } = useOrgUser(userId);
  const hasAssessments =
    useProductVisibility("assessments") === ProductVisibility.Enabled;
  const hasLearnCast =
    useProductVisibility("learncast") === ProductVisibility.Enabled;

  const isMe = userId === user.id;
  const isManagerOrAdmin =
    user.directReports.some((dr) => dr.id === userId) ||
    user.permissions.includes("org-admin");

  if (!isFetched || !userFetched) {
    return <Loader />;
  }

  const contentOfType = (type: ContentType) =>
    userContent.filter((x) => x.type === type);

  const assigner = (type: ContentType) =>
    isManagerOrAdmin
      ? () =>
          setShareConfig({
            type,
            targetId: userId,
            itemId: null,
            targetType: "user",
          })
      : null;

  return (
    <CenterColumn>
      {shareConfig && (
        <ShareModal config={shareConfig} onClose={() => setShareConfig(null)} />
      )}

      <PageHeader
        text={isMe ? "My content" : `${profileUser.name}'s content`}
        subtitle={`A summary of ${
          isMe ? "your" : "this user's"
        } content progress`}
      />

      <ContentSection
        heading="Mindset"
        assignLabel="Assign mindset"
        onAssign={assigner("module")}
      >
        <Panel>
          <ContentDetailsTable
            items={sortBy(contentOfType("module"), "name")}
            contentType="module"
          />
        </Panel>
      </ContentSection>

      <ContentSection
        heading="Lessons"
        assignLabel="Assign lesson"
        onAssign={assigner("lesson")}
      >
        <LessonDetailsTable items={contentOfType("lesson")} />
      </ContentSection>

      {hasAssessments && (
        <ContentSection
          heading="Assessments"
          assignLabel="Assign assessment"
          onAssign={assigner("assessment")}
        >
          <Panel>
            <ContentDetailsTable
              items={sortBy(contentOfType("assessment"), "name")}
              contentType="assessment"
            />
          </Panel>
        </ContentSection>
      )}

      {hasLearnCast && (
        <ContentSection
          heading="LearnCast"
          assignLabel="Assign LearnCast"
          onAssign={assigner("learncast")}
        >
          <Panel>
            <ContentDetailsTable
              items={sortBy(contentOfType("learncast"), "orderIndex")}
              contentType="learncast"
            />
          </Panel>
        </ContentSection>
      )}
    </CenterColumn>
  );
};
